import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Edit2, Eye, Upload, Loader2, X, Check, Image as ImageIcon } from 'lucide-react';
import { COMMUNICATION_STATUS_OPTIONS, CommStatusBadge } from '../communicationConfig';
import { CABLE_MEDIA_TYPES, CABLE_CATEGORIES, CABLE_SHIELDING, CABLE_JACKET_RATINGS, COMMON_CONNECTORS } from './cableTypesConfig';

const NONE = '__none__';

const EMPTY_FORM = {
  name: '', mediaType: '', category: '', shielding: '', jacketRating: '',
  pairCoreCount: '', maxLengthM: '', connectorCompatibility: [],
  manufacturer: '', modelNumber: '', approved: false, status: '', notes: '',
};

const toForm = (it) => ({
  name: it.name || '', mediaType: it.mediaType || '', category: it.category || '',
  shielding: it.shielding || '', jacketRating: it.jacketRating || '',
  pairCoreCount: it.pairCoreCount ?? '', maxLengthM: it.maxLengthM ?? '',
  connectorCompatibility: it.connectorCompatibility || [],
  manufacturer: it.manufacturer || '', modelNumber: it.modelNumber || '',
  approved: !!it.approved, status: it.status || '', notes: it.notes || '',
});

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};

const OptionalSelect = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? '' : v)}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={NONE}>Not set</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const CableTypesSection = ({ projectId, csiItem, canEditCSIItems, canManageProject }) => {
  const csiItemId = csiItem?.id;
  const base = `${API}/communication-systems/${projectId}/structured-cabling/cable-types`;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [connectorInput, setConnectorInput] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!projectId || !csiItemId) return;
    try {
      const res = await axios.get(`${base}?csi_item_id=${csiItemId}`);
      setItems(res.data);
    } catch {
      toast.error('Failed to load cable types');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const resetImageState = () => { setPendingImageFile(null); setPendingImagePreview(null); setRemoveExistingImage(false); };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setErrors({}); resetImageState(); setConnectorInput(''); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm(toForm(it)); setErrors({}); resetImageState(); setConnectorInput(''); setModalOpen(true); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Cable Type is required';
    if (!form.mediaType) e.mediaType = 'Media Type is required';
    if (!form.category) e.category = 'Category / Fiber Class is required';
    if (form.pairCoreCount !== '' && Number(form.pairCoreCount) < 0) e.pairCoreCount = 'Cannot be negative';
    if (form.maxLengthM !== '' && Number(form.maxLengthM) < 0) e.maxLengthM = 'Cannot be negative';
    if ((form.notes || '').length > 1000) e.notes = 'Maximum 1000 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { toast.error('Please fix the highlighted fields'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        csiItemId,
        name: form.name.trim(),
        pairCoreCount: form.pairCoreCount === '' ? null : parseInt(form.pairCoreCount, 10),
        maxLengthM: form.maxLengthM === '' ? null : parseFloat(form.maxLengthM),
      };
      let saved;
      if (editing) {
        saved = (await axios.put(`${base}/${editing.id}`, payload)).data;
      } else {
        saved = (await axios.post(base, payload)).data;
      }
      if (editing && removeExistingImage && !pendingImageFile && editing.productImage) {
        await axios.delete(`${base}/${saved.id}/image`);
      }
      if (pendingImageFile) {
        const fd = new FormData();
        fd.append('file', pendingImageFile);
        await axios.post(`${base}/${saved.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(editing ? 'Cable type updated' : 'Cable type created');
      setModalOpen(false);
      setEditing(null);
      fetchItems();
    } catch (err) {
      const msg = errMsg(err, 'Failed to save cable type');
      if (err?.response?.status === 409) setErrors((e) => ({ ...e, name: msg }));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${base}/${deleteTarget.id}`);
      setItems((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setSelectedIds((prev) => prev.filter((id) => id !== deleteTarget.id));
      toast.success('Cable type deleted');
    } catch (err) {
      toast.error(errMsg(err, 'Failed to delete cable type'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${base}/bulk-delete`, { ids: selectedIds });
      const removed = new Set(selectedIds);
      setItems((prev) => prev.filter((i) => !removed.has(i.id)));
      setSelectedIds([]);
      toast.success('Selected cable types deleted');
    } catch (err) {
      toast.error(errMsg(err, 'Failed to delete selected'));
    } finally {
      setBulkConfirm(false);
    }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = () => setSelectedIds((prev) => (prev.length === items.length ? [] : items.map((i) => i.id)));

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setPendingImageFile(file);
    setPendingImagePreview(URL.createObjectURL(file));
    setRemoveExistingImage(false);
  };

  const clearImage = () => {
    if (pendingImageFile) { setPendingImageFile(null); setPendingImagePreview(null); }
    else { setRemoveExistingImage(true); }
  };

  const addConnector = (val) => {
    const v = (val || '').trim();
    if (!v) return;
    if (!form.connectorCompatibility.includes(v)) set('connectorCompatibility', [...form.connectorCompatibility, v]);
    setConnectorInput('');
  };
  const removeConnector = (tag) => set('connectorCompatibility', form.connectorCompatibility.filter((t) => t !== tag));

  const imgPreview = pendingImagePreview || (!removeExistingImage && editing?.productImage?.url) || null;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="comm-cable-types-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-700">Cable Types <span className="text-slate-400 font-normal">({items.length})</span></h3>
        <div className="flex gap-2">
          {canEditCSIItems && selectedIds.length > 0 && (
            <Button onClick={() => setBulkConfirm(true)} className="bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="cable-type-bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-1.5" />Delete Selected ({selectedIds.length})
            </Button>
          )}
          {canEditCSIItems && (
            <Button onClick={openCreate} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="cable-type-create-btn">
              <Plus className="h-4 w-4 mr-1.5" />Add Cable Type
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-center py-14 text-slate-500 border-2 border-dashed rounded-lg" data-testid="cable-type-empty">
          <p>No cable types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canEditCSIItems && (
                  <th className="w-10 px-3 py-3 text-left">
                    <Checkbox checked={selectedIds.length === items.length && items.length > 0} onCheckedChange={toggleSelectAll} data-testid="cable-type-select-all" />
                  </th>
                )}
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Cable Type</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Media Type</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Category / Fiber Class</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Shielding</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Jacket Rating</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700">Approved</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Status</th>
                <th className="w-28 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className={`border-b hover:bg-slate-50 transition-colors ${selectedIds.includes(it.id) ? 'bg-orange-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`} data-testid={`cable-type-row-${it.id}`}>
                  {canEditCSIItems && (
                    <td className="px-3 py-2"><Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={() => toggleSelect(it.id)} data-testid={`cable-type-select-${it.id}`} /></td>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {it.productImage?.thumbnailUrl ? (
                        <img src={it.productImage.thumbnailUrl} alt="" className="h-8 w-8 rounded object-cover border border-slate-200 shrink-0" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0"><ImageIcon className="h-4 w-4 text-slate-300" /></div>
                      )}
                      <span className="font-medium text-slate-900">{it.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{it.mediaType || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{it.category || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{it.shielding || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{it.jacketRating || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{it.manufacturer || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{it.modelNumber || '-'}</td>
                  <td className="px-3 py-2 text-center">{it.approved ? <Check className="h-4 w-4 text-emerald-600 inline" /> : <span className="text-slate-300">-</span>}</td>
                  <td className="px-3 py-2"><CommStatusBadge status={it.status} /></td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:bg-orange-500 hover:text-white" onClick={() => setViewTarget(it)} data-testid={`cable-type-view-${it.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      {canEditCSIItems && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(it)} data-testid={`cable-type-edit-${it.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>}
                      {canManageProject && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => setDeleteTarget(it)} data-testid={`cable-type-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={(o) => { if (!o) { setModalOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cable-type-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Cable Type' : 'Add Cable Type'}</DialogTitle>
            <DialogDescription>Define a reusable cable type for this Communication System.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Product Image */}
            <div className="space-y-2">
              <Label>Product Image</Label>
              {imgPreview ? (
                <div className="flex items-center gap-3">
                  <img src={imgPreview} alt="preview" className="h-24 w-24 rounded object-cover border border-slate-200" data-testid="cable-type-image-preview" />
                  <Button type="button" variant="outline" size="sm" onClick={clearImage} data-testid="cable-type-image-remove"><X className="h-3.5 w-3.5 mr-1.5" />Remove</Button>
                </div>
              ) : (
                <label className="cursor-pointer inline-block">
                  <div className="border-2 border-dashed rounded-lg px-6 py-5 text-center hover:bg-slate-50 transition-colors">
                    <Upload className="h-6 w-6 mx-auto mb-1.5 text-slate-400" />
                    <p className="text-xs text-slate-500">Upload Image (JPG, PNG)</p>
                  </div>
                  <input type="file" className="hidden" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={onPickImage} data-testid="cable-type-image-input" />
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cable Type *</Label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={120} placeholder="e.g. CAT6A-PLENUM-BLU" className={errors.name ? 'border-red-500' : ''} data-testid="cable-type-input-name" />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label>Media Type *</Label>
                <Select value={form.mediaType} onValueChange={(v) => set('mediaType', v)}>
                  <SelectTrigger className={errors.mediaType ? 'border-red-500' : ''} data-testid="cable-type-select-mediaType"><SelectValue placeholder="Select media type" /></SelectTrigger>
                  <SelectContent>{CABLE_MEDIA_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                {errors.mediaType && <p className="text-xs text-red-500">{errors.mediaType}</p>}
              </div>
              <div className="space-y-2">
                <Label>Category / Fiber Class *</Label>
                <Select value={form.category} onValueChange={(v) => set('category', v)}>
                  <SelectTrigger className={errors.category ? 'border-red-500' : ''} data-testid="cable-type-select-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{CABLE_CATEGORIES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                {errors.category && <p className="text-xs text-red-500">{errors.category}</p>}
              </div>
              <div className="space-y-2">
                <Label>Shielding</Label>
                <OptionalSelect value={form.shielding} onChange={(v) => set('shielding', v)} placeholder="Select shielding" options={CABLE_SHIELDING} testId="cable-type-select-shielding" />
              </div>
              <div className="space-y-2">
                <Label>Jacket Rating</Label>
                <OptionalSelect value={form.jacketRating} onChange={(v) => set('jacketRating', v)} placeholder="Select jacket rating" options={CABLE_JACKET_RATINGS} testId="cable-type-select-jacketRating" />
              </div>
              <div className="space-y-2">
                <Label>Pair / Core Count</Label>
                <Input type="number" min="0" value={form.pairCoreCount} onChange={(e) => set('pairCoreCount', e.target.value)} placeholder="e.g. 4" className={errors.pairCoreCount ? 'border-red-500' : ''} data-testid="cable-type-input-pairCoreCount" />
                {errors.pairCoreCount && <p className="text-xs text-red-500">{errors.pairCoreCount}</p>}
              </div>
              <div className="space-y-2">
                <Label>Max Length (meters)</Label>
                <Input type="number" min="0" step="0.1" value={form.maxLengthM} onChange={(e) => set('maxLengthM', e.target.value)} placeholder="e.g. 90" className={errors.maxLengthM ? 'border-red-500' : ''} data-testid="cable-type-input-maxLengthM" />
                {errors.maxLengthM && <p className="text-xs text-red-500">{errors.maxLengthM}</p>}
              </div>
              <div className="space-y-2">
                <Label>Manufacturer</Label>
                <Input value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} placeholder="e.g. CommScope" data-testid="cable-type-input-manufacturer" />
              </div>
              <div className="space-y-2">
                <Label>Model Number</Label>
                <Input value={form.modelNumber} onChange={(e) => set('modelNumber', e.target.value)} placeholder="e.g. 760123456" data-testid="cable-type-input-modelNumber" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status || NONE} onValueChange={(v) => set('status', v === NONE ? '' : v)}>
                  <SelectTrigger data-testid="cable-type-select-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    {COMMUNICATION_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Connector Compatibility */}
            <div className="space-y-2">
              <Label>Connector Compatibility</Label>
              <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="cable-type-connector-tags">
                {form.connectorCompatibility.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-full border border-slate-200">
                    {tag}
                    <button type="button" onClick={() => removeConnector(tag)} className="hover:text-red-600" data-testid={`cable-type-connector-remove-${tag}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <Input
                value={connectorInput}
                onChange={(e) => setConnectorInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addConnector(connectorInput); } }}
                placeholder="Type a connector and press Enter (e.g. RJ45, LC)"
                data-testid="cable-type-connector-input"
              />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {COMMON_CONNECTORS.filter((c) => !form.connectorCompatibility.includes(c)).map((c) => (
                  <button key={c} type="button" onClick={() => addConnector(c)} className="text-xs text-orange-600 border border-orange-200 rounded-full px-2 py-0.5 hover:bg-orange-50">+ {c}</button>
                ))}
              </div>
            </div>

            {/* Approved */}
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.approved} onCheckedChange={(c) => set('approved', !!c)} data-testid="cable-type-check-approved" />
              <span className="text-sm">Approved</span>
            </label>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} maxLength={1000} rows={3} placeholder="Additional notes..." className={errors.notes ? 'border-red-500' : ''} data-testid="cable-type-input-notes" />
              {errors.notes && <p className="text-xs text-red-500">{errors.notes}</p>}
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" onClick={() => { setModalOpen(false); setEditing(null); }} data-testid="cable-type-cancel-btn">Cancel</Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="cable-type-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{editing ? 'Update' : 'Create'} Cable Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View details */}
      <Dialog open={!!viewTarget} onOpenChange={(o) => { if (!o) setViewTarget(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="cable-type-view-modal">
          <DialogHeader><DialogTitle>{viewTarget?.name}</DialogTitle><DialogDescription>Cable type details</DialogDescription></DialogHeader>
          {viewTarget && (
            <div className="space-y-4">
              {viewTarget.productImage?.url && <img src={viewTarget.productImage.url} alt="" className="max-h-48 rounded border border-slate-200 object-contain" />}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ['Media Type', viewTarget.mediaType], ['Category / Fiber Class', viewTarget.category],
                  ['Shielding', viewTarget.shielding], ['Jacket Rating', viewTarget.jacketRating],
                  ['Pair / Core Count', viewTarget.pairCoreCount], ['Max Length (m)', viewTarget.maxLengthM],
                  ['Manufacturer', viewTarget.manufacturer], ['Model Number', viewTarget.modelNumber],
                ].map(([k, v]) => (
                  <div key={k}><p className="text-xs text-slate-400 uppercase tracking-wide">{k}</p><p className="text-slate-800">{v !== '' && v !== null && v !== undefined ? v : '—'}</p></div>
                ))}
                <div><p className="text-xs text-slate-400 uppercase tracking-wide">Approved</p><p className="text-slate-800">{viewTarget.approved ? 'Yes' : 'No'}</p></div>
                <div><p className="text-xs text-slate-400 uppercase tracking-wide">Status</p><CommStatusBadge status={viewTarget.status} /></div>
                <div className="col-span-2"><p className="text-xs text-slate-400 uppercase tracking-wide">Connector Compatibility</p><p className="text-slate-800">{(viewTarget.connectorCompatibility || []).join(', ') || '—'}</p></div>
                <div className="col-span-2"><p className="text-xs text-slate-400 uppercase tracking-wide">Notes</p><p className="text-slate-800 whitespace-pre-wrap">{viewTarget.notes || '—'}</p></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm (single) */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="cable-type-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete cable type?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete &quot;{deleteTarget?.name}&quot;. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent data-testid="cable-type-bulk-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.length} cable type(s)?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the selected cable types. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
